import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Globe, Menu, X, Shield, ExternalLink, CheckCircle, MessageCircle } from 'lucide-react';
import Logo from './Logo';
import ConsultationPopup from './ConsultationPopup';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';

const Header = () => {
  const { t, language, changeLanguage, direction, languages } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${window.scrollY}px`;
    } else {
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
    };
  }, [isMobileMenuOpen]);

  const scrollToSection = (id) => {
    setIsMobileMenuOpen(false);
    setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const navLinks = [
    { id: 'hero', label: t.nav.home },
    { id: 'about', label: t.nav.about },
    { id: 'services', label: t.nav.services },
    { id: 'cases', label: t.nav.cases },
    { id: 'how-it-works', label: t.nav.howItWorks },
    { id: 'faq', label: t.nav.faq },
    { id: 'contact', label: t.nav.contact },
  ];

  const whatsappNumber = '447366524092';
  const whatsappMessage = encodeURIComponent(
    direction === 'rtl' 
      ? 'مرحباً، أريد الاستفسار عن خدمات استرداد الأموال' 
      : 'Hello, I would like to inquire about fund recovery services'
  );
  const whatsappLink = `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`;

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? 'bg-white/95 backdrop-blur-md shadow-lg py-2'
            : 'bg-transparent py-4'
        }`}
        dir={direction}
      >
        <div className="max-w-7xl mx-auto px-4 lg:px-6">
          <div className={`flex items-center justify-between ${direction === 'rtl' ? 'flex-row-reverse' : ''}`}>
            
            {/* Logo & Brand - Enhanced */}
            <div 
              className={`flex items-center gap-4 cursor-pointer group ${direction === 'rtl' ? 'flex-row-reverse' : ''}`} 
              onClick={() => scrollToSection('hero')}
            >
              {/* Premium Logo Container */}
              <div className="relative">
                {/* Glow effect on hover */}
                <div className="absolute -inset-2 bg-gradient-to-r from-[#C9A55A]/20 to-[#D4AF37]/20 rounded-full blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <Logo size="default" className="relative z-10 transform group-hover:scale-105 transition-transform duration-300" />
              </div>
              
              {/* Brand Name - More Prominent */}
              <div className={`${direction === 'rtl' ? 'text-right' : 'text-left'}`}>
                <h1 className={`text-2xl md:text-3xl font-bold transition-colors duration-300 ${
                  isScrolled ? 'text-[#0A1628]' : 'text-white'
                }`} style={{ fontFamily: 'Georgia, serif' }}>
                  {t.ui.lawyerName}
                </h1>
                <div className={`flex items-center gap-2 ${direction === 'rtl' ? 'flex-row-reverse justify-end' : ''}`}>
                  <span className={`text-sm font-medium transition-colors duration-300 ${
                    isScrolled ? 'text-[#C9A55A]' : 'text-[#C9A55A]'
                  }`}>
                    {t.ui.lawyerTitle}
                  </span>
                </div>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className={`hidden lg:flex items-center gap-1 ${direction === 'rtl' ? 'flex-row-reverse' : ''}`}>
              {navLinks.map((link) => (
                <button
                  key={link.id}
                  onClick={() => scrollToSection(link.id)}
                  className={`px-3 py-2 text-sm font-medium rounded-lg transition-all duration-300 hover:bg-[#C9A55A]/10 ${
                    isScrolled
                      ? 'text-gray-700 hover:text-[#C9A55A]'
                      : 'text-white/90 hover:text-white'
                  }`}
                >
                  {link.label}
                </button>
              ))}
            </nav>

            {/* Right Side Actions */}
            <div className={`flex items-center gap-3 ${direction === 'rtl' ? 'flex-row-reverse' : ''}`}>
              
              {/* SRA License Badge - Professional Design */}
              <a
                href="https://www.sra.org.uk/consumers/register/person/?sraNumber=649806&prevSearchText=Qasim%20hussain&prevSearchFilter="
                target="_blank"
                rel="noopener noreferrer"
                className={`hidden md:flex items-center gap-2 px-4 py-2 rounded-full border-2 transition-all duration-300 group ${
                  isScrolled 
                    ? 'border-emerald-500/50 bg-emerald-50 hover:bg-emerald-100 hover:border-emerald-500' 
                    : 'border-emerald-400/50 bg-emerald-500/10 hover:bg-emerald-500/20 hover:border-emerald-400'
                }`}
                data-testid="sra-license-badge"
              >
                {/* Verified Icon */}
                <div className="relative">
                  <Shield className={`w-5 h-5 ${isScrolled ? 'text-emerald-600' : 'text-emerald-400'}`} />
                  <CheckCircle className={`w-3 h-3 absolute -bottom-0.5 -right-0.5 ${isScrolled ? 'text-emerald-600' : 'text-emerald-400'}`} />
                </div>
                
                {/* Text */}
                <span className={`text-sm font-semibold ${isScrolled ? 'text-emerald-700' : 'text-emerald-300'}`}>
                  {t.ui.verifyLicense}
                </span>
                
                {/* External Link Icon */}
                <ExternalLink className={`w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity ${isScrolled ? 'text-emerald-600' : 'text-emerald-400'}`} />
              </a>
              
              {/* Language Selector */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={`rounded-full w-10 h-10 ${
                      isScrolled
                        ? 'text-gray-700 hover:bg-gray-100'
                        : 'text-white hover:bg-white/10'
                    }`}
                  >
                    <Globe className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  {languages.map((lang) => (
                    <DropdownMenuItem
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`flex items-center gap-3 cursor-pointer ${
                        language === lang.code ? 'bg-[#C9A55A]/10 text-[#C9A55A]' : ''
                      }`}
                    >
                      <span className="text-lg">{lang.flag}</span>
                      <span>{lang.name}</span>
                      {language === lang.code && (
                        <CheckCircle className="w-4 h-4 ml-auto text-[#C9A55A]" />
                      )}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* CTA Button with Popup */}
              <ConsultationPopup>
                <button
                  className={`hidden md:inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold text-sm transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 ${
                    isScrolled
                      ? 'bg-gradient-to-r from-[#C9A55A] to-[#D4AF37] text-[#0A1628]'
                      : 'bg-gradient-to-r from-[#C9A55A] to-[#D4AF37] text-[#0A1628]'
                  }`}
                  data-testid="header-consultation-btn"
                >
                  <span>{t.nav.consultation}</span>
                </button>
              </ConsultationPopup>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className={`lg:hidden p-2 rounded-lg transition-colors ${
                  isScrolled ? 'text-gray-700' : 'text-white'
                }`}
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu - Full Screen Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-[100] lg:hidden">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          
          {/* Menu Container */}
          <div 
            className="absolute top-0 left-0 right-0 bottom-0 bg-[#0A1628] overflow-y-auto"
            dir={direction}
          >
            {/* Header */}
            <div className="sticky top-0 z-10 bg-[#0A1628] border-b border-white/10 px-4 py-4">
              <div className={`flex items-center justify-between ${direction === 'rtl' ? 'flex-row-reverse' : ''}`}>
                <div className={`flex items-center gap-3 ${direction === 'rtl' ? 'flex-row-reverse' : ''}`}>
                  <Logo size="small" />
                  <div className={direction === 'rtl' ? 'text-right' : ''}>
                    <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Georgia, serif' }}>
                      {t.ui.lawyerName}
                    </h2>
                    <p className="text-xs text-[#C9A55A]">
                      {t.ui.lawyerTitle}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            
            {/* Menu Content */}
            <div className="px-4 py-6 space-y-4">
              {/* SRA License Badge - Mobile */}
              <a
                href="https://www.sra.org.uk/consumers/register/person/?sraNumber=649806&prevSearchText=Qasim%20hussain&prevSearchFilter="
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-3 px-4 py-4 rounded-xl border-2 border-emerald-400/30 bg-emerald-500/10 hover:bg-emerald-500/20 transition-colors"
              >
                <Shield className="w-5 h-5 text-emerald-400" />
                <span className="font-semibold text-emerald-300">
                  {t.ui.verifySRALicense}
                </span>
                <ExternalLink className="w-4 h-4 text-emerald-400" />
              </a>
              
              {/* WhatsApp Button - Mobile */}
              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-3 px-4 py-4 rounded-xl bg-gradient-to-r from-[#25D366] to-[#128C7E] text-white font-semibold hover:shadow-lg hover:shadow-[#25D366]/30 transition-all"
              >
                <MessageCircle className="w-5 h-5" />
                <span>{t.ui.chatWhatsApp}</span>
              </a>
              
              {/* Navigation Links */}
              <nav className="space-y-1 pt-4 border-t border-white/10">
                {navLinks.map((link) => (
                  <button
                    key={link.id}
                    onClick={() => scrollToSection(link.id)}
                    className={`block w-full px-4 py-4 rounded-xl text-white hover:bg-white/10 transition-colors ${direction === 'rtl' ? 'text-right' : 'text-left'}`}
                  >
                    {link.label}
                  </button>
                ))}
              </nav>
              
              {/* CTA Button */}
              <div className="pt-4 border-t border-white/10">
                <ConsultationPopup>
                  <button
                    className="w-full px-6 py-4 bg-gradient-to-r from-[#C9A55A] to-[#D4AF37] text-[#0A1628] rounded-xl font-bold shadow-lg"
                    data-testid="mobile-consultation-btn"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {t.nav.consultation}
                  </button>
                </ConsultationPopup>
              </div>
              
              {/* Language Selector */}
              <div className="pt-4 border-t border-white/10">
                <p className={`text-sm text-gray-400 mb-3 ${direction === 'rtl' ? 'text-right' : ''}`}>
                  {t.ui.selectLanguage}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        changeLanguage(lang.code);
                        setIsMobileMenuOpen(false);
                      }}
                      className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-colors ${
                        language === lang.code 
                          ? 'bg-[#C9A55A]/20 border border-[#C9A55A]/50 text-[#C9A55A]' 
                          : 'bg-white/5 border border-white/10 text-white hover:bg-white/10'
                      }`}
                    >
                      <span className="text-lg">{lang.flag}</span>
                      <span className="text-sm">{lang.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Floating SRA Button */}
      <a
        href="https://www.sra.org.uk/consumers/register/person/?sraNumber=649806"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-44 left-6 z-[60] group"
        data-testid="sra-button"
      >
        {/* Pulse Animation */}
        <div className="absolute inset-0 bg-[#003366] rounded-full animate-ping opacity-20" />
        
        {/* Button */}
        <div className="relative flex items-center gap-3 px-4 py-3 bg-white border-2 border-[#003366] rounded-full shadow-lg hover:shadow-xl hover:shadow-[#003366]/30 transition-all duration-300 hover:scale-105">
          {/* SRA Official Logo */}
          <svg className="w-8 h-8" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* SRA Logo - Official Style */}
            <circle cx="50" cy="50" r="45" fill="#003366"/>
            <text x="50" y="42" textAnchor="middle" fill="white" fontSize="24" fontWeight="bold" fontFamily="Arial, sans-serif">SRA</text>
            <text x="50" y="62" textAnchor="middle" fill="#C9A55A" fontSize="8" fontFamily="Arial, sans-serif">REGULATED</text>
            <path d="M25 70 L75 70" stroke="#C9A55A" strokeWidth="1"/>
          </svg>
          
          {/* Text - Shows on hover/desktop */}
          <span className="hidden sm:inline text-[#003366] font-bold whitespace-nowrap text-sm">
            {direction === 'rtl' ? 'تحقق من الترخيص' : 'Verify License'}
          </span>
        </div>
        
        {/* Tooltip for mobile */}
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1 bg-[#003366] text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap sm:hidden">
          SRA
        </div>
      </a>

      {/* Floating WhatsApp Button */}
      <a
        href={whatsappLink}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-24 left-6 z-[60] group"
        data-testid="whatsapp-button"
      >
        {/* Pulse Animation */}
        <div className="absolute inset-0 bg-[#25D366] rounded-full animate-ping opacity-30" />
        
        {/* Button */}
        <div className="relative flex items-center gap-3 px-5 py-4 bg-gradient-to-r from-[#25D366] to-[#128C7E] rounded-full shadow-lg hover:shadow-xl hover:shadow-[#25D366]/30 transition-all duration-300 hover:scale-105">
          {/* WhatsApp Icon */}
          <svg className="w-7 h-7 text-white" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
          
          {/* Text - Shows on hover/desktop */}
          <span className="hidden sm:inline text-white font-semibold whitespace-nowrap">
            {t.ui.chatWithUs}
          </span>
        </div>
        
        {/* Tooltip for mobile */}
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1 bg-[#0A1628] text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap sm:hidden">
          WhatsApp
        </div>
      </a>
    </>
  );
};

export default Header;
